import { zodResolver } from '@hookform/resolvers/zod';
import {
  DunningCreateFormInput,
  DunningCreateSchema,
  DunningStepCreateFormInput,
  IDunningCreateSchema,
  IDunningStepCreateSchema,
  IDunnings,
} from '@monetize/types/app';
import pick from 'lodash/pick';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import {
  useCreateEntity,
  useGetById,
  useUpdateEntity,
} from '../../../../api/queryUtils';
import {
  MBox,
  MButton,
  MFormField,
  MGrid,
  MHStack,
  MInput,
  MLink,
  MPageContainer,
  MPageLoader,
  MSettingsPageHeader,
  MText,
  MTooltip,
} from '../../../../components/Monetize';
import { CustomSteps } from '../../../../components/Monetize/CustomSteps/CustomSteps';
import { MTooltipContentList } from '../../../../components/Monetize/MTooltipContentList';
import { ROUTES } from '../../../../constants';
import { defaultStepValue } from '../../../../constants/dunnings';
import { useCloneDunning } from '../../../../hooks/useCloneDunning';
import { usePrompt } from '../../../../hooks/usePrompt';
import { useDocumentHead } from '../../../../services/documentHead';
import { logger } from '../../../../services/logger';
import { convertDunningRequestFromResponse } from './dunning.utils';
import DunningStep from './DunningStep';

const getErrorMessages = (value: unknown): string[] => {
  if (!value || typeof value !== 'object') {
    return [];
  }

  const current = value as { message?: unknown };
  const messages =
    typeof current.message === 'string' && current.message.trim().length > 0
      ? [current.message.trim()]
      : [];

  Object.values(value as Record<string, unknown>).forEach((nested) => {
    messages.push(...getErrorMessages(nested));
  });

  return messages;
};

const EditDunning = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { setDocTitle } = useDocumentHead();
  const dunningId = params.dunningId || '';

  const {
    data: dunning,
    isLoading,
    isError,
  } = useGetById<IDunnings>('dunnings', dunningId, {
    refetchOnWindowFocus: false,
    enabled: !!dunningId,
    meta: { showErrorToast: true },
  });

  useEffect(() => {
    if (dunning) {
      setExistingSteps(dunning.dunningSteps);
      setDocTitle('Settings', `Dunning - ${dunning.name}`);
    } else if (isError) {
      navigate(ROUTES.SETTINGS_DUNNING_LIST);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dunning, isError]);

  const [existingSteps, setExistingSteps] = useState<
    IDunningStepCreateSchema[]
  >(() => dunning?.dunningSteps || []);

  const { mutate: createDunningMutate, isPending: createLoading } =
    useCreateEntity<IDunnings, IDunningCreateSchema>('dunnings', {
      onSuccess: (newData) => {
        newData.id && navigate(-1);
      },
      meta: { showErrorToast: true },
    });

  const { mutate: updateDunningMutate, isPending: updateLoading } =
    useUpdateEntity<IDunnings, IDunningCreateSchema>('dunnings', {
      onSuccess: (newData) => {
        /** Using setTimeout here since it was triggering usePrompt hook because of the dirty fields on save */
        newData.id && setTimeout(() => navigate(-1));
      },
      meta: { showErrorToast: true },
    });

  const { mutate: cloneDunning, isPending: isCloningDunning } = useCloneDunning(
    {
      onSuccess: (clonedDunning) => {
        if (clonedDunning.id) {
          window.open(ROUTES.getDunningEditRoute(clonedDunning.id), '_blank');
        }
      },
    },
  );

  const handleCloneDunning = () => {
    if (dunningId) {
      cloneDunning(dunningId);
    }
  };

  const dunningData = dunning
    ? pick(dunning, Object.keys(DunningCreateSchema.shape))
    : undefined;

  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    clearErrors,
    watch,
    formState: { errors, dirtyFields, isDirty, isValid },
  } = useForm<DunningCreateFormInput, unknown, IDunningCreateSchema>({
    resolver: zodResolver(DunningCreateSchema),
    mode: 'onChange',
    values: dunningData
      ? {
          ...dunningData,
          name: dunningData.name || '',
          dunningSteps:
            dunningData.dunningSteps?.map(
              ({ internalCCEmails, internalBCCEmails, ...rest }) => {
                return {
                  ...rest,
                  /** Since BE can send array, FE needs to turn it to string */
                  internalCCEmails: Array.isArray(internalCCEmails)
                    ? internalCCEmails.join(', ')
                    : null,
                  internalBCCEmails: Array.isArray(internalBCCEmails)
                    ? internalBCCEmails.join(', ')
                    : null,
                };
              },
            ) || [],
        }
      : undefined,
  });

  usePrompt(
    'There are unsaved changes, do you want to discard these changes?',
    !!dunningId && Object.keys(dirtyFields).length > 0,
  );

  const { fields, append, replace } = useFieldArray({
    control,
    name: `dunningSteps`,
    keyName: 'sId',
  });

  useEffect(() => {
    if (fields.length === 0) {
      addNewDunningStep(1);
    }
  }, []);

  const onSubmit = async (data: IDunningCreateSchema) => {
    const finalSteps = data.dunningSteps.map(
      (step: IDunningStepCreateSchema) => {
        const { id, ...rest } = convertDunningRequestFromResponse(step);
        return rest;
      },
    );

    const payload = { ...data, dunningSteps: finalSteps };

    createDunningMutate(payload);
  };

  const onUpdate = async (data: IDunningCreateSchema) => {
    const submittedSteps = data.dunningSteps;

    const finalSteps = submittedSteps.map((step: IDunningStepCreateSchema) => {
      const updatedStepsId = existingSteps?.find(
        (eStep) => eStep.id === step.id,
      );
      if (updatedStepsId) {
        return convertDunningRequestFromResponse(step);
      }
      const { id, ...rest } = convertDunningRequestFromResponse(step);
      return rest;
    });

    const finalPayload = { ...data, id: dunningId, dunningSteps: finalSteps };

    updateDunningMutate({
      id: dunningId,
      payload: finalPayload,
    });
  };

  const handleSubmitBtn = (data: IDunningCreateSchema) => {
    if (dunningId) {
      return onUpdate(data);
    }
    return onSubmit(data);
  };

  const addNewDunningStep = (stepSequence?: number | null): void => {
    if (fields.length === 0) {
      append({
        ...defaultStepValue,
        id: uuidv4(),
        stepSequence: !stepSequence ? fields.length + 1 : stepSequence,
      });
    } else {
      const {
        sendEmail,
        retryCollection,
        emailBillGroupShippingContact,
        emailBillGroupCCEmails,
        emailBillGroupBCCEmails,
        emailAccountBCCEmails,
        internalCCEmails,
        internalBCCEmails,
      } = watch(`dunningSteps.${fields.length - 1}`);

      append({
        ...defaultStepValue,
        sendEmail,
        retryCollection,
        emailBillGroupShippingContact,
        emailBillGroupCCEmails,
        emailBillGroupBCCEmails,
        emailAccountBCCEmails,
        internalCCEmails,
        internalBCCEmails,
        id: uuidv4(),
        stepSequence: !stepSequence ? fields.length + 1 : stepSequence,
      });
    }
  };

  const removeDunningStep = (id: number | string) => {
    clearErrors();
    const steps = getValues('dunningSteps');
    const filteredSteps = steps.filter((step) => step.id !== id);

    const updatedSteps = filteredSteps.map((step, index: number) => {
      const updatedStepId = { ...step, stepSequence: index + 1 };
      return updatedStepId;
    });

    replace(updatedSteps);
  };

  const swapSteps = (
    items: DunningStepCreateFormInput[],
    firstIndex: number,
    secondIndex: number,
  ) => {
    clearErrors();
    const results = items.slice();
    const firstItem = items[firstIndex];
    results[firstIndex] = items[secondIndex];
    results[secondIndex] = firstItem;

    return results;
  };

  const reOrderDunningStep = (data: any, orderType: string) => {
    clearErrors();
    const dunningSteps = getValues('dunningSteps');

    const selectedIndex = dunningSteps.findIndex((step) => step.id === data.id);

    const fromIndex = selectedIndex;

    let toIndex = 0;
    if (orderType === 'up') {
      toIndex = fromIndex - 1;
    }
    if (orderType === 'down') {
      toIndex = fromIndex + 1;
    }

    if (fromIndex !== toIndex) {
      const result = swapSteps(dunningSteps, fromIndex, toIndex);

      const updatedSteps = result.map((step, index: number) => {
        const updateStepSequence = { ...step, stepSequence: index + 1 };
        return updateStepSequence;
      });

      replace(updatedSteps);
    }
  };

  const onError = (error: unknown) => logger.error('Dunning save error', error);

  const saveValidationMessages = useMemo(() => {
    if (!isDirty) {
      return ['Make a change to save'];
    }

    if (!isValid) {
      const messages = [...new Set(getErrorMessages(errors))];
      return messages.length > 0
        ? messages
        : ['Complete required fields to save'];
    }

    return [];
  }, [errors, isDirty, isValid]);

  const isSaveDisabled = createLoading || updateLoading || !isDirty || !isValid;
  const isSaveReasonVisible =
    isSaveDisabled && !createLoading && !updateLoading;

  if (isLoading) {
    return <MPageLoader />;
  }

  return (
    <MPageContainer
      alignItems="stretch"
      data-testid="dunning-form"
      overflowX="scroll"
    >
      <MSettingsPageHeader
        divider={false}
        hasBackButton
        title={!dunningId ? 'New Dunning Process' : 'Edit Dunning Process'}
        id={dunningId}
        recordAuditInfo={dunning}
      >
        <MHStack gap="4">
          {dunningId && (
            <MButton
              variant="secondary"
              isLoading={isCloningDunning}
              isDisabled={isCloningDunning}
              onClick={handleCloneDunning}
            >
              Clone
            </MButton>
          )}
          <MTooltip
            shouldWrapChildren
            isDisabled={!isSaveReasonVisible}
            bg="tPurple.base"
            label={
              <MTooltipContentList
                title="To save, resolve required actions below"
                listItems={saveValidationMessages}
              />
            }
          >
            <MButton
              data-testid="dunning-form-submit-btn"
              variant="primary"
              isLoading={createLoading || updateLoading}
              isDisabled={isSaveDisabled}
              onClick={handleSubmit(handleSubmitBtn, onError)}
            >
              Save
            </MButton>
          </MTooltip>
        </MHStack>
      </MSettingsPageHeader>
      <MBox>
        <MGrid
          maxWidth="750px"
          templateColumns="2fr 1fr 1fr"
          gap={4}
          mb={10}
          px={3.5}
        >
          <MFormField error={errors.name} label="Dunning Name" isRequired>
            <Controller
              name="name"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <MInput placeholder="Enter Name" {...field} />
              )}
            />
          </MFormField>
        </MGrid>
        <MBox mb={6} px={3.5}>
          <MText fontSize={18} fontWeight="bold">
            Dunning Steps
          </MText>
          <MText color="tGray.darkPurple">
            {
              "Configure the steps you'd like to take when a customer's payment method fails."
            }
          </MText>
        </MBox>

        <MBox ml={-4}>
          {fields.map((field, index) => (
            <CustomSteps
              key={field.sId}
              stepIndex={index}
              stepNumber={field.stepSequence}
              stepData={field}
              totalSteps={fields?.length}
              removable={fields?.length !== 1}
              removeStep={removeDunningStep}
              isOrderable={fields?.length !== 1}
              reOrderSteps={reOrderDunningStep}
            >
              <DunningStep
                errors={errors}
                index={index}
                control={control}
                setValue={setValue}
                clearErrors={clearErrors}
              />
            </CustomSteps>
          ))}
          <CustomSteps
            stepNumber={fields.length + 1}
            isLastStep
            stepData={defaultStepValue}
            removable={false}
            containerProps={fields.length > 1 ? { ml: '0.6rem', gap: 2.5 } : {}}
          >
            <MLink
              color="tIndigo.base"
              fontWeight="bold"
              textDecoration="underline"
              fontSize="sm"
              onClick={() => addNewDunningStep()}
            >
              + Add New Step
            </MLink>
          </CustomSteps>
        </MBox>
      </MBox>
    </MPageContainer>
  );
};

export default EditDunning;
